import { EventListeners, MinzeElement, Reactive, Attrs } from 'minze'
import { config, Font } from '../../constants'
import { Callback } from '../../modules/callback'
import { Locale } from '../../modules/locale'
import { AdditionalTypes } from '../../modules/additionalTypes'
import { escapeHtml } from '../../modules/utils'
import axios from 'redaxios'
import { Language } from '../../../types/global'

Locale.getInstance().registerKeySet('fr', 'lvc-languages-select-field', {
  title: 'Langues*',
  placeholder: "Recherche d'une langue",
  choose: 'Choisir',
})

Locale.getInstance().registerKeySet('en', 'lvc-languages-select-field', {
  title: 'Languages*',
  placeholder: 'Search for a language',
  choose: 'Choose',
})

export interface LvcLanguagesSelectField {
  title: string
  placeholder: string
  open: boolean
  onChange: string
  defaultValue: any
  value: string
}

export class LvcLanguagesSelectField extends MinzeElement {
  private languages: any[] = []
  private selectedIds: string[] = []
  private filter: string = ''
  attrs: Attrs = [
    'title',
    'placeholder',
    ['default-value', ''],
    ['on-change', ''],
    ['error', false],
  ]

  reactive: Reactive = [['value', '']]

  static observedAttributes = ['title', 'on-change', 'error', 'default-value']

  async onBlur(e: Event): Promise<void> {
    this.open = false
    this.rerender()
  }

  onAttributeChange(
    name?: string | undefined,
    oldValue?: string | null | undefined,
    newValue?: string | null | undefined
  ): void | Promise<void> {
    if (name === 'default-value') {
      this.updateDefaultValue()
    }
  }

  async updateDefaultValue() {
    if (this.defaultValue && this.defaultValue !== true) {
      this.selectedIds = JSON.parse(decodeURIComponent(this.defaultValue)).map(
        (k: any) => `${k.id}`
      )
    }
    this.rerender()
  }

  async onReady() {
    try {
      const response = await axios.get<Language[]>(`${config.backendApi}/content/getLanguages`)

      if (response.data) {
        this.languages = this.sortLanguages(response.data)
        this.rerender()
      }
    } catch (error) {
      console.error('Error fetching languages:', error)
    }
  }

  async onToggle() {
    this.open = !this.open
    this.rerender()
  }

  onLanguageClick(e: Event): void {
    let value = (e.target as HTMLElement).getAttribute('data-id')

    if (!value) {
      return
    }
    let index = this.selectedIds.indexOf(value)

    if (index > -1) {
      this.selectedIds.splice(index, 1)
    } else {
      this.selectedIds.push(value)
    }

    this.serializeValue()
    this.rerender()
  }

  async onFilterChange(e: Event) {
    this.filter = (e.target as HTMLInputElement).value
    this.rerender()
  }

  sortLanguages(languages: Language[]): Language[] {
    return languages.sort((a, b) => {
      // First, sort by displayOrder if available
      if (a.displayOrder !== null && b.displayOrder !== null) {
        return a.displayOrder - b.displayOrder
      }

      // If one has displayOrder and the other doesn't, prioritize the one with displayOrder
      if (a.displayOrder !== null) return -1
      if (b.displayOrder !== null) return 1

      // If neither has displayOrder or both are null, sort alphabetically by labelEn
      const currentLocale = Locale.getInstance().getLanguage()
      const labelA = currentLocale === 'fr' ? a.labelFr : a.labelEn
      const labelB = currentLocale === 'fr' ? b.labelFr : b.labelEn

      return labelA.localeCompare(labelB, currentLocale)
    })
  }

  serializeValue() {
    let objects = this.languages.filter(k => this.selectedIds.indexOf(`${k.id}`) > -1)
    this.value = encodeURIComponent(JSON.stringify(objects))
    Callback.call(this.onChange, objects)
    this.cast('languages-field:change', this.value)
  }

  getLanguagesLabel() {
    if (this.selectedIds.length === 0) {
      return Locale.getKey('lvc-languages-select-field.choose')
    }
    const userLanguage = Locale.language

    let labels = this.languages
      .filter(k => this.selectedIds.indexOf(`${k.id}`) > -1)
      .map(k => k?.[userLanguage === 'fr' ? 'labelFr' : 'labelEn'])

    if (labels.length === 1) {
      return labels[0]
    }

    if (labels.length > 1) {
      return labels[0] + ' + ' + (labels.length - 1)
    }
  }

  renderLanguage = (language: any) => {
    const userLanguage = Locale.language

    if (
      this.filter &&
      language?.[userLanguage === 'fr' ? 'labelFr' : 'labelEn']
        .toLowerCase()
        .indexOf(this.filter.toLowerCase()) === -1
    ) {
      return ''
    }

    let selectedIdsIndex = this.selectedIds.indexOf(`${language.id}`)
    let isNotSelected = selectedIdsIndex == -1

    return `
      <button 
        name="language"
        class="${isNotSelected ? 'not-selected' : 'selected'}" 
        data-id="${language.id}"
        data-order=${!isNotSelected ? selectedIdsIndex + 1 : null}>
        ${language?.[userLanguage === 'fr' ? 'labelFr' : 'labelEn']}
      </button>
    `
  }

  renderLanguages = () => {
    let html = ''

    for (let i = 0; i < this.languages.length; i++) {
      html += this.renderLanguage(this.languages[i])
    }

    return html
  }

  html = () => {
    let showError = typeof this.error === 'string' && this.error.length > 0

    return `
      <div class="title">${this.title || Locale.getKey('lvc-languages-select-field.title')}</div>
      <div class="input ${this.selectedIds?.length ? 'has-value' : ''}">
        <button class="dropbtn" name="toggle">
          ${this.getLanguagesLabel()}
          <lvc-chevron-down-icon></lvc-chevron-down-icon>
        </button>
        <div class="overlay ${this.open ? 'show' : ''}" name="overlay"></div>
        <div class="dropdown-content ${this.open ? 'show' : ''}">
          <div class="header">
            <input name="filter" type="text" value="${escapeHtml(this.filter)}" placeholder="${this.placeholder || Locale.getKey('lvc-languages-select-field.placeholder')}">
            <lvc-chevron-up-icon></lvc-chevron-up-icon>
          </div>
          <div class="languages" data-selected="${this.selectedIds.length}">
            ${this.renderLanguages()}
          </div>
        </div>
       
      </div> 
      <lvc-box>
        ${showError ? `<lvc-error error="${this.error}"></lvc-error>` : ''}
      </lvc-box>
    `
  }

  css = () => {
    return `
      :host {
      }

      .overlay {
        position: fixed;
        top: 0px;
        left: 0px;
        bottom: 0px;
        right: 0px;
        width: 100%;
        height: 100%;
        z-index: 1;
        display: none;
      }
      .overlay.show {
        display: block;
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 16px;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        border-top-left-radius: 24px;
        border-top-right-radius: 24px;
        border-bottom: 1px solid var(--Minuit, #1E007D);
        background: var(--Ciel, #BFF0FF);

      }


      input[name="filter"] {
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 0;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        background: transparent;
      }

      .title {
        color: var(--Minuit, #1E007D);
        font-variant-numeric: lining-nums tabular-nums;
        font-feature-settings: 'clig' off, 'liga' off;

        /* Body 3 */
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        margin-bottom: 3.5px;
      }

      .input {
        display: flex;
        height: 48px;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        flex-shrink: 0;
        align-self: stretch;
        border: 1px solid var(--Minuit, #1E007D);
        position: relative;
        border-radius: 24px;
      }
      
      .input.has-value {
        border: 1px solid rgba(30, 0, 125, 0.3);
        background: #BFF0FF;
      }

      .languages {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: stretch;
        overflow: hidden;
        overflow-y: auto;
        max-height: calc(300px - 48px);
        padding: 0 10px;
      }


      .languages button {
        display: flex;
        justify-content: space-between;
        font-family: ${Font.family};
        font-size: 14px;
        line-height: 32px;
        cursor: pointer;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        padding: 8px 8px;
        border: none;
        height: 48px;
        flex: 0 0 48px;
        border-bottom: 1px solid rgba(30, 0, 125, 0.30);
      }

      .languages button:last-child {
        border-bottom: none;
      }

      .languages button.selected {
      }

      .languages button.selected::after {
        content: attr(data-order);
        text-align: center;
        border-radius: 50%;
        width: 24px;
        height: 24px;
        margin-top: 3px;
        background: var(--brand-minuit, #1E007D);
        color: var(--brand-spotlight, #FF4600);
        font-variant-numeric: lining-nums tabular-nums;
        font-feature-settings: 'clig' off, 'liga' off;
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 24px;
      }

      /* style.css */

      /* Dropdown Button */
      .dropbtn {
        color: white;
        padding: 12px 16px;
        font-size: 14px;
        font-weight: 500;
        border: none;
        cursor: pointer;
        flex: 1;
        width: 100%;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      /* Dropdown button on hover & focus */
      .dropbtn:hover, .dropbtn:focus {
      }

      /* Dropdown Content */
      .dropdown-content {
        width: calc(100% + 2px);
        margin: -1px;
        display: none;
        position: absolute;
        background-color: #f9f9f9;
        min-width: 160px;
        box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
        z-index: 2;
        top: 0;
        border-radius: 24px; 
        max-height: 300px;
        min-height: 96px;
        border: 1px solid var(--Minuit, #1E007D);
        line-height: 32px;
      }

      .dropdown-content.show {
        display: block;
      }


    `
  }

  eventListeners: EventListeners = [
    ['button[name="toggle"]', 'click', this.onToggle.bind(this)],
    ['button[name="language"]', 'click', this.onLanguageClick.bind(this)],
    ['input[name="filter"]', 'keyup', this.onFilterChange.bind(this)],
    [this, 'blur', this.onBlur.bind(this)],
    ['div.overlay', 'click', this.onBlur.bind(this)],
  ]
}
